import { useState } from 'react';
import type { CurrentSessionClose } from '#shared/types';
import { Banner } from '#client/components/ui/Banner';
import { Modal } from '#client/components/ui/Modal';
import { Spinner } from '#client/components/ui/Spinner';
import { TextArea } from '#client/components/ui/TextArea';
import { useApiResource } from '#client/hooks/useResourceItem';
import { useMutation } from '#client/hooks/useMutation';
import { formatDateTime } from '#client/lib/format';
import { ENDPOINT } from '#client/lib/endpoints';
import { useAuth } from '#client/providers/AuthProvider';
import { DrawerFigures } from './DrawerFigures';
import { DAY_CLOSE_REMARK_MAX_LENGTH } from '#shared/types';

/**
 * The Day Close at the door.
 *
 * Signing out is the moment a shift ends, so it is where the staff member is
 * asked to confirm what they took. Submitting is encouraged, not forced: a
 * sign-out that can be refused just teaches people to close the tab, and the
 * admin's reconciliation screen shows every session that ended without one.
 */
export function SignOutDialog({ onClose }: { onClose: () => void }) {
  const { signOut, submitDayClose } = useAuth();
  const session = useApiResource<CurrentSessionClose>(ENDPOINT.dayCloseSession);
  const [comment, setComment] = useState('');
  const dayClose = useMutation('The Day Close was not submitted.');
  // Set once signing out has begun, by either button: the dialog stays locked
  // until the session is gone.
  const [isSigningOut, setIsSigningOut] = useState(false);
  const busy = dayClose.isPending || isSigningOut;

  async function handleSubmit() {
    const outcome = await dayClose.run(() => submitDayClose(comment));
    if (outcome.ok) setIsSigningOut(true);
  }

  function handleSkip() {
    setIsSigningOut(true);
    void signOut();
  }

  return (
    <Modal
      open
      title="Close your day"
      onClose={busy ? () => undefined : onClose}
      primaryAction={{
        label: 'Submit and sign out',
        onAction: () => void handleSubmit(),
        loading: busy,
      }}
      secondaryAction={{ label: 'Sign out without submitting', onAction: handleSkip }}
    >
      <div className="c-form-stack">
        {session.loading && <Spinner label="Adding up your session" showLabel />}
        {session.error && <Banner tone="warning">{session.error}</Banner>}
        {session.data && (
          <>
            <p>
              Your figures since you signed in at {formatDateTime(session.data.session.started_at)}.
            </p>
            <DrawerFigures figures={session.data.figures} />
          </>
        )}
        <TextArea
          label="Comment"
          helpText="Explain any shortfall or discrepancy in the drawer. Leave it blank if everything matches."
          value={comment}
          maxLength={DAY_CLOSE_REMARK_MAX_LENGTH}
          onChange={(event) => setComment(event.target.value)}
        />
        {dayClose.errorMessage && <Banner tone="critical">{dayClose.errorMessage}</Banner>}
      </div>
    </Modal>
  );
}
